import React, { useEffect, useState } from 'react';
import useAuth from '../hooks/useAuth.js';
import axiosInstance from '../utilities/axiosInstance';
import { useNavigate } from 'react-router-dom';
import "../styles/profileDetails.scss";
import { useSearch } from '../context/SearchContext';
import commonFunction from '../utilities/commonFunction';
import Loader from './Loader.js';

function Tagger() {
    const { auth } = useAuth();
    const { searchValue } = useSearch();

    const [allProjects, setAllProjects] = useState([]);
    const [filteredProjects, setFilteredProjects] = useState([]);
    const [pageSize, setPageSize] = useState(12);
    const [currentPage, setCurrentPage] = useState(1);
    const [recordsPerPage, setRecordsPerPage] = useState([]);
    const [totalPages, setTotalPages] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();

    const getTaggerProjects = async () => {
        try {
            setIsLoading(true);
            const res = await axiosInstance.get("/taggerproject", {
                params: {
                    profile_id: auth.profile_id,
                    profile_role: auth.profile_role,
                },
            });
            const allProjects = res.data.reverse() || [];

            const updatedProjects = await Promise.all(
                allProjects.map(async (project) => {
                    const newProject = { ...project };

                    if (newProject.task_mediatype === "doc" && newProject.first_doc_path) {
                        try {
                            const imageData = await commonFunction.convertFirstPageOfPdfToImage(newProject.first_doc_path);
                            newProject.previewImage = imageData;
                        } catch (err) {
                            console.error("Failed to convert PDF:", err);
                            newProject.previewImage = null;
                        }
                    }

                    return newProject;
                })
            );
            setAllProjects(updatedProjects);
        } catch (error) {
            console.error("Failed to fetch projects:", error);
        }finally{
            setIsLoading(false);
        }
    }

    useEffect(() => {
        getTaggerProjects();

    }, [auth.profile_id, auth.profile_role]);

    useEffect(() => {
        let currentFiltered = allProjects;

        if (searchValue) {
            currentFiltered = currentFiltered.filter(project =>
                project.project_Name.toLowerCase().includes(searchValue.toLowerCase()) ||
                (project.project_clientname && project.project_clientname.toLowerCase().includes(searchValue.toLowerCase()))
            );
        }

        setFilteredProjects(currentFiltered);
        setTotalPages(Math.ceil(currentFiltered.length / pageSize));
        setCurrentPage(1);
    }, [searchValue, allProjects, pageSize]);

    useEffect(() => {
        let start = (currentPage - 1) * pageSize;
        let end = start + pageSize;
        let records = filteredProjects?.slice(start, end);
        setRecordsPerPage(records);
    }, [currentPage, filteredProjects, pageSize]);

    const handleOnCardClick = (project) => {
        navigate(`/tagger/tasks?project_id=${project.project_id}`, { state: { previousRoute: '/tagger', projectId: project.project_id } })
    }

    const handleNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    }

    const handlePrevPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    }

    const handlePageClick = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    }

    const getVisiblePages = (total, current) => {
        if (total <= 5) return Array.from({ length: total }, (_, i) => i + 1);
        const pages = [];
        if (current <= 3) {
            pages.push(1, 2, 3, '...', total);
        } else if (current >= total - 2) {
            pages.push(1, '...', total - 2, total - 1, total);
        } else {
            pages.push(1, '...', current, '...', total);
        }
        return pages;
    };

    return (
        <div>
            <h2 className="font-poppins font-medium text-[20px] leading-[100%] tracking-[0%] text-gray-main pb-3 border-b border-gray-d-200 dark:text-white dark:border-white">Welcome {(auth.profile_name !== 'null') ? auth.profile_name : auth.profile_username} - Tagger Page</h2>

            {/* The local search input box has been removed */}

            <div>
                <div className="cardContainer grid grid-cols-4 gap-4 mt-8">
                    {recordsPerPage && recordsPerPage.length > 0 ? (
                        recordsPerPage.map((project, index) => (
                            <div className="p-2 border border-light-blue rounded-[10px] relative dark:bg-gray-main" key={index}>
                                <div className="w-full h-[112px] bg-slate-400 rounded-t-[10px] cursor-pointer" onClick={() => handleOnCardClick(project)}>
                                    {project.task_mediatype == "image" && project?.first_image_path && (
                                        <img className="rounded-t-[10px] object-cover h-[112px] w-full" src={project.first_image_path} alt={project.first_image_path} />
                                    )}
                                    {project.task_mediatype == "doc" && project?.first_doc_path && (
                                        <img className="rounded-t-[10px] object-cover h-[112px] w-full" src={project.previewImage} alt={project.previewImage} />
                                    )}
                                </div>
                                <div className=" px-[4.5px] py-[11px]">
                                    <div className="project-name flex justify-between items-center">
                                        <h3 className="font-poppins font-semibold text-[15px] leading-[100%] tracking-[-0.01em] dark:text-white">
                                            {project?.project_Name.length > 14 ? project.project_Name.slice(0, 14) + "..." : project.project_Name}
                                        </h3>
                                    </div>
                                    <div className="created-date flex justify-between mt-[11px]">
                                        <div className="font-poppins text-gray-d-100 dark:text-slate-400 font-semibold text-[13px] leading-[18px] tracking-[-0.01em]">
                                            Created On : {new Date(project?.createdDate).toLocaleDateString("en-US")}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        !isLoading && <div className="col-span-4 text-center text-gray-600 dark:text-gray-400">
                            No projects found.
                        </div>
                    )}
                </div>
            </div>
            <div className=" flex justify-center">
                {filteredProjects?.length > 0 && 
                    <div className="paginationContainer flex justify-center mt-6">
                        <div className="flex items-center space-x-2 dark:text-white">
                            <button
                                type="button"
                                disabled={currentPage === 1}
                                onClick={() => handlePrevPage()}
                                className={`w-10 h-10 rounded-full border border-gray-300 text-lg flex items-center justify-center ${currentPage === 1 ? 'cursor-not-allowed text-gray-400' : 'hover:bg-gray-100 dark:text-gray-main'
                                    }`}
                            >{'<'}</button>
                            {/* Pagination numbers */}
                            <div className="flex space-x-2">
                                {getVisiblePages(totalPages, currentPage).map((page, index) => (
                                    <button
                                        key={index}
                                        onClick={() => typeof page === 'number' && handlePageClick(page)}
                                        className={`w-10 h-10 rounded-full border border-gray-300 text-lg flex items-center justify-center ${
                                            currentPage === page
                                                ? 'bg-gray-300 font-semibold dark:text-gray-main'
                                                : 'hover:bg-gray-100'
                                        } ${typeof page !== 'number' ? 'cursor-default text-gray-400' : ''}`}
                                        disabled={typeof page !== 'number'}
                                    >
                                        {page}
                                    </button>
                                ))}
                            </div>
                            <button
                                type="button"
                                disabled={currentPage === totalPages}
                                onClick={() => handleNextPage()} className={`w-10 h-10 rounded-full border border-gray-300 text-lg flex items-center justify-center ${currentPage === totalPages ? 'cursor-not-allowed text-gray-400' : 'hover:bg-gray-100'
                                    }`}>{'>'}</button>
                        </div>
                    </div>}
            </div>
            <Loader loading={isLoading}/>
        </div>
    )
}

export default Tagger;